
import { Component, ChangeDetectionStrategy, input, output, signal, effect, inject, computed, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms'; 
import { Plan } from '../../models/index';
import { BillingService } from '../../services/billing.service';
import { NotificationService } from '../../services/notification.service';

// Declare Omise global to prevent TS errors
declare var Omise: any;

@Component({
  selector: 'app-payment-gateway-modal',
  standalone: true,
  imports: [CommonModule, DecimalPipe, FormsModule],
  templateUrl: './payment-gateway-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentGatewayModalComponent implements OnInit {
  private billingService = inject(BillingService);
  private notificationService = inject(NotificationService);

  isOpen = input.required<boolean>();
  plan = input.required<Plan | null>();
  
  closeModal = output<void>();
  paymentComplete = output<void>();

  currentStep = signal<'select' | 'transfer' | 'card'>('select');
  isProcessing = signal(false);
  
  // Bank Transfer State
  isCheckingSlip = signal(false);
  verificationError = signal<string | null>(null);
  uploadedSlip = signal<File | null>(null);
  uploadedFileName = signal<string>('');

  // Card Form State
  cardName = signal('');
  cardNumber = signal('');
  expiryMonth = signal('');
  expiryYear = signal('');
  securityCode = signal('');
  omiseError = signal<string | null>(null);

  // Config - Updated to match Omise Test Dashboard Screenshot
    readonly PLATFORM_BANK_NAME = "ธนาคารกสิกรไทย (KBANK)";
    readonly PLATFORM_ACCOUNT_NAME = "นาย ศุภชัย สวาทใจ";
    readonly PLATFORM_ACCOUNT_NUMBER = "013-8-99647-6";
    readonly PLATFORM_PROMPTPAY_PHONE_FOR_QR = "0819042240";
  
  readonly OMISE_PUBLIC_KEY = "pkey_test_666elsv9bqpezrltv1z"; 

  qrCodeUrl = computed(() => {
    const amount = this.plan()?.price_monthly || 0;
    return `https://promptpay.io/${this.PLATFORM_PROMPTPAY_PHONE_FOR_QR}/${amount}.png`;
  });

  constructor() {
    effect(() => {
      if(this.isOpen()) {
        this.resetState();
        this.loadOmiseScript();
      }
    });
  }

  ngOnInit() {
    this.loadOmiseScript();
  }

  private loadOmiseScript() {
    if (typeof Omise !== 'undefined') {
        Omise.setPublicKey(this.OMISE_PUBLIC_KEY);
        return;
    }
    const src = 'https://cdn.omise.co/omise.js';
    if (document.querySelector(`script[src="${src}"]`)) return; 

    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
        if (typeof Omise !== 'undefined') {
            Omise.setPublicKey(this.OMISE_PUBLIC_KEY);
            this.omiseError.set(null); 
        }
    };
    script.onerror = () => {
        this.omiseError.set('ไม่สามารถโหลดระบบชำระเงินได้ (กรุณาปิด AdBlock หรือตรวจสอบอินเทอร์เน็ต)');
    };
    document.head.appendChild(script);
  }

  resetState() {
      this.currentStep.set('select');
      this.isProcessing.set(false);
      this.isCheckingSlip.set(false);
      this.uploadedSlip.set(null);
      this.uploadedFileName.set('');
      this.verificationError.set(null);
      this.cardName.set('');
      this.cardNumber.set('');
      this.expiryMonth.set('');
      this.expiryYear.set('');
      this.securityCode.set('');
      this.omiseError.set(null);
  }

  selectBankTransfer() { this.currentStep.set('transfer'); }
  selectCreditCard() { this.currentStep.set('card'); this.loadOmiseScript(); }

  submitCardPayment() {
      this.omiseError.set(null);
      if (typeof Omise === 'undefined') {
          this.omiseError.set("ระบบชำระเงินกำลังโหลด... กรุณากดชำระเงินอีกครั้งใน 2-3 วินาที");
          return;
      }
      this.isProcessing.set(true);

      let year = parseInt(this.expiryYear());
      if (year < 100) year += 2000; 

      const cardObject = {
          name: this.cardName(),
          number: this.cardNumber().replace(/\s+/g, ''), 
          expiration_month: parseInt(this.expiryMonth()),
          expiration_year: year,
          security_code: this.securityCode()
      };

      Omise.createToken('card', cardObject, async (statusCode: number, response: any) => {
          if (statusCode === 200) {
              try {
                  const amount = this.plan()?.price_monthly || 0;
                  await this.billingService.processOmiseCharge(response.id, amount, {
                      planId: this.plan()?.id,
                      planName: this.plan()?.name
                  });
                  this.paymentComplete.emit();
                  this.onClose();
                  this.notificationService.showSuccess('ชำระเงินสำเร็จ! ขอบคุณที่ร่วมเป็นพาร์ทเนอร์กับเรา');
              } catch (e: any) {
                  this.notificationService.handleError(e);
                  this.omiseError.set("การประมวลผลล้มเหลว กรุณาตรวจสอบวงเงินบัตร");
              } finally {
                  this.isProcessing.set(false);
              }
          } else {
              let errorMsg = response.message || 'ข้อมูลบัตรไม่ถูกต้อง';
              if (errorMsg.includes('expiration date')) errorMsg = 'บัตรหมดอายุแล้ว';
              else if (errorMsg.includes('number is invalid')) errorMsg = 'เลขบัตรเครดิตไม่ถูกต้อง';
              this.omiseError.set(errorMsg);
              this.isProcessing.set(false);
          }
      });
  }

  copyAccountNumber() {
      const rawNumber = this.PLATFORM_ACCOUNT_NUMBER.replace(/-/g, '');
      navigator.clipboard.writeText(rawNumber).then(() => {
          this.notificationService.showInfo('คัดลอกเลขบัญชีเรียบร้อยแล้ว');
      });
  }

  onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
          const file = input.files[0];
          if (file.size > 5 * 1024 * 1024) {
              this.notificationService.showWarning("ไฟล์สลิปใหญ่เกินไป (สูงสุด 5MB)");
              return;
          }
          this.uploadedSlip.set(file);
          this.uploadedFileName.set(file.name);
      }
  }

  async verifySlip() {
    const file = this.uploadedSlip();
    if (!file || !this.plan()) return;
    this.isProcessing.set(true);
    this.isCheckingSlip.set(true);
    try {
        const base64 = await this.fileToBase64(file);
        const result = await this.billingService.verifySlipWithApi(base64, this.plan()!.price_monthly, this.plan()!.id);
        if (result.success) {
            this.paymentComplete.emit();
            this.onClose();
            this.notificationService.showSuccess('✅ ตรวจสอบสลิปสำเร็จ! แพ็กเกจของคุณได้รับการอัปเดตแล้ว');
        } else {
            this.verificationError.set(result.message || "ตรวจสอบสลิปไม่ผ่าน");
            this.notificationService.showError(result.message || "สลิปไม่ถูกต้อง");
        }
    } catch (e) {
        this.notificationService.handleError(e);
    } finally {
        this.isProcessing.set(false);
        this.isCheckingSlip.set(false);
    }
  }

  private fileToBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
      });
  }

  onClose() { this.closeModal.emit(); }
  onBackdropClick(event: MouseEvent) { if (event.target === event.currentTarget) this.onClose(); }
}
